import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ObjectDetails from './object/ObjectDetails';

import { objectsData, buttonsData } from './object/objects';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Container = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f2e1e5;
    position: relative;
    padding-left: 35vw;

    @media (max-width: 768px) {
        padding-left: 0;
    }
`;

const BackgroundImage = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 35vw;
    height: 100%;
    background-image: url('/apartment.jpg');
    background-size: cover;
    background-position: center;
    opacity: 1;
    z-index: 0;
    pointer-events: none;
    border-top-right-radius: 160px;
    border-bottom-right-radius: 160px;

    @media (max-width: 768px) {
        width: 100%;
        opacity: 0.2;
        border-radius: 0;
    }
`;

const GlassEffect = `
    background-color: rgba(255, 255, 255, 0.01);
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
`;

const Header = styled.header`
    width: fit-content;
    margin: 20px auto 10px;
    padding: 10px 20px;
    border-radius: 36px;
    text-align: center;
    ${GlassEffect}
    position: relative;
    z-index: 10;
`;

const Title = styled.h1`
    margin: 0;
    color: #bf919c;
    font-size: clamp(2rem, 5vw, 4rem);
    font-family: wellhome, sans-serif;
    cursor: pointer;
`;

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(191, 145, 156, 0.4); }
    70% { box-shadow: 0 0 0 15px rgba(191, 145, 156, 0); }
    100% { box-shadow: 0 0 0 0 rgba(191, 145, 156, 0); }
`;

const ObjectsButton = styled.button`
    display: flex;
    align-items: center;
    gap: 15px;
    margin: 10px auto 20px;
    padding: 8px 25px 8px 8px;
    border-radius: 50px;
    background: white;
    border: 2px solid #bf919c;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    z-index: 20;
    position: relative;
    animation: ${pulse} 2s infinite;
`;

const MosaicIcon = styled.div`
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 2px;
    overflow: hidden;
    border: 1px solid #bf919c;
`;

const MosaicTile = styled.div`
    background-image: url(${props => props.src});
    background-size: cover;
    background-position: center;
    background-color: #eee;
`;

const ButtonText = styled.span`
    color: #bf919c;
    font-family: unbounded, sans-serif;
    font-size: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

const Main = styled.main`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    position: relative;
    z-index: 1;
    width: 100%;
    box-sizing: border-box;
`;

const ButtonsWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    max-width: 800px;
    width: 100%;
    margin: 0 auto;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const BigButton = styled.button`
    ${GlassEffect}
    border-radius: 40px;
    padding: 20px 15px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    transition: transform 0.3s ease, background-color 0.3s ease;
    border: 1px solid transparent;
    font-family: unbounded, sans-serif;
    width: 100%;
    box-sizing: border-box;

    &:hover {
        transform: translateY(-5px);
        background-color: rgba(255, 255, 255, 0.2);
    }
`;

const ButtonImage = styled.img`
    width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 20px;
`;

const ButtonLabel = styled.span`
    font-size: 1.1rem;
    font-weight: 500;
    color: #bf919c;
    text-align: center;
    line-height: 1.4;
    word-wrap: break-word;
    white-space: normal;
    max-width: 180px;
`;

const ModalOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(5px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled(motion.div)`
    background: white;
    padding: 40px;
    border-radius: 30px;
    max-width: 500px;
    width: 90%;
    position: relative;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalTitle = styled.h2`
    color: #bf919c;
    font-family: unbounded, sans-serif;
`;

const ListContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 20px;
`;

const ListItem = styled.div`
    padding: 15px;
    border-radius: 15px;
    background: #fdf6f7;
    cursor: pointer;
    transition: background 0.2s;
    font-family: unbounded, sans-serif;
    color: #bf919c;
    border: 1px solid #eee;

    &:hover {
        background: #f2e1e5;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 20px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #bf919c;
    z-index: 1001;
`;

const NormalText = styled.p`
    font-size: 1.25rem;
    line-height: 1.6;
    color: #444;
    margin: 0;
    font-family: unbounded, sans-serif;
`;

const Footer = styled.footer`
    width: fit-content;
    margin: 20px auto 20px;
    padding: 10px 20px;
    border-radius: 32px;
    text-align: center;
    ${GlassEffect}
    position: relative;
    z-index: 10;
`;

const ContactBox = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    flex-wrap: wrap;
    justify-content: center;
`;

const ContactLink = styled.a`
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.7;
    }
`;

const ContactIcon = styled.img`
    width: 24px;
    height: 24px;
    object-fit: contain;
`;

const ContactText = styled.span`
    color: #555;
    font-weight: 500;
`;

const TypewriterText = ({ text }) => {
    const [displayedText, setDisplayedText] = useState('');

    useEffect(() => {
        let i = 0;
        setDisplayedText('');
        const timer = setInterval(() => {
            if (i < text.length) {
                setDisplayedText((prev) => prev + text.charAt(i));
                i++;
            } else {
                clearInterval(timer);
            }
        }, 40);
        return () => clearInterval(timer);
    }, [text]);

    return <NormalText>{displayedText}</NormalText>;
};

const Modal = ({ isOpen, onClose, title, children }) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <ModalOverlay
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <ModalContent
                        initial={{ scale: 0.8, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.8, opacity: 0, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CloseButton onClick={onClose}>&times;</CloseButton>
                        {title && <ModalTitle>{title}</ModalTitle>}
                        {children}
                    </ModalContent>
                </ModalOverlay>
            )}
        </AnimatePresence>
    );
};

const Contact = ({ icon, text, href }) => (
    <ContactLink href={href} target={href?.startsWith('http') ? "_blank" : undefined} rel="noopener noreferrer">
        <ContactIcon src={icon} />
        <ContactText>{text}</ContactText>
    </ContactLink>
);

const App = () => {
    const [currentPage, setCurrentPage] = useState('home');
    const [selectedObject, setSelectedObject] = useState(null);
    const [isObjectsModalOpen, setIsObjectsModalOpen] = useState(false);
    const [infoModal, setInfoModal] = useState({ isOpen: false, text: '' });
    const [currency, setCurrency] = useState('UAH');
    const [rates, setRates] = useState({ USD: 40, EUR: 43 });

    useEffect(() => {
        const fetchRates = async () => {
            try {
                const response = await fetch('https://bank.gov.ua/NBUStatService/v1/statistichny-danі/exchange?json');
                const data = await response.json();
                const usd = data.find(item => item.cc === 'USD')?.rate || 40;
                const eur = data.find(item => item.cc === 'EUR')?.rate || 43;
                setRates({ USD: usd, EUR: eur });
            } catch (error) {
                console.error("Failed to fetch exchange rates", error);
            }
        };
        fetchRates();
    }, []);

    const toggleCurrency = () => {
        const flow = { 'UAH': 'USD', 'USD': 'EUR', 'EUR': 'UAH' };
        setCurrency(flow[currency]);
    };

    const navigateToDetails = (obj) => {
        setSelectedObject(obj);
        setIsObjectsModalOpen(false);
        setCurrentPage('details');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleHomeClick = () => {
        setCurrentPage('home');
        setSelectedObject(null);
    };

    return (
        <Container>
            <BackgroundImage />

            <Header>
                <Title onClick={handleHomeClick}>Майстерня Оренди</Title>
            </Header>

            <ObjectsButton onClick={() => setIsObjectsModalOpen(true)}>
                <MosaicIcon>
                    <MosaicTile src="/object/nl/nl1.jpg" />
                    <MosaicTile src="/object/nl/nl2.jpg" />
                    <MosaicTile src="/object/nl/nl3.jpg" />
                    <MosaicTile src="/apartment.jpg" />
                </MosaicIcon>
                <ButtonText>Наші Об'єкти</ButtonText>
            </ObjectsButton>

            <Main>
                <AnimatePresence mode="wait">
                    {currentPage === 'home' ? (
                        <motion.div
                            key="home"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                        >
                            <ButtonsWrapper>
                                {buttonsData.map((btn) => (
                                    <BigButton
                                        key={btn.id}
                                        onClick={() => setInfoModal({ isOpen: true, text: btn.text })}
                                    >
                                        <ButtonImage src={btn.img} alt={btn.label} />
                                        <ButtonLabel>{btn.label}</ButtonLabel>
                                    </BigButton>
                                ))}
                            </ButtonsWrapper>
                        </motion.div>
                    ) : (
                        <ObjectDetails
                            selectedObject={selectedObject}
                            currency={currency}
                            toggleCurrency={toggleCurrency}
                            rates={rates}
                        />
                    )}
                </AnimatePresence>
            </Main>

            <Footer>
                <ContactBox>
                    <Contact
                        icon="/icon/phone.png"
                        text="+38 093 688 89 96"
                        href="tel:+380936888996"
                    />
                    <Contact
                        icon="/icon/instagram.png"
                        text="@maisternya.orendy"
                        href="https://www.instagram.com/maisternya.orendy"
                    />
                    <Contact
                        icon="/icon/email.png"
                        text="maisternya.orendy@gmail.com"
                        href="mailto:maisternya.orendy@gmail.com"
                    />
                </ContactBox>
            </Footer>

            <Modal
                isOpen={isObjectsModalOpen}
                onClose={() => setIsObjectsModalOpen(false)}
                title="Оберіть об'єкт"
            >
                <ListContent>
                    {objectsData.map(obj => (
                        <ListItem key={obj.id} onClick={() => navigateToDetails(obj)}>
                            {obj.name}
                        </ListItem>
                    ))}
                </ListContent>
            </Modal>

            <Modal
                isOpen={infoModal.isOpen}
                onClose={() => setInfoModal({ isOpen: false, text: '' })}
            >
                <TypewriterText text={infoModal.text} />
            </Modal>
        </Container>
    );
};

export default App;